import React from "react";
import PropTypes from "prop-types";
import { Box, Divider, Grid, IconButton, Modal, Stack, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, Clock, Link2, X } from "lucide-react";

import { AnnouncementDescription } from "./AnnouncementDescription";
import { AnnouncementAudienceSummary } from "./AnnouncementAudienceFields";
import {
  AGENCY_SCOPE_ALL,
  AGENCY_SCOPE_MANUAL,
  CLIENT_MODE_ALL,
  CLIENT_MODE_NONE,
  CLIENT_MODE_SELECTED,
} from "./announcementAudience";
import { Button } from "../../../components/Button";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { useTheme } from "../../../theme/useTheme";

const normalizeUrlWithHttps = (url) => {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/\//, "")}`;
};

const getAnnouncementImageUrl = (announcement) => {
  if (!announcement) return null;
  const possibleKeys = [
    "header_image_url",
    "image_url",
    "image",
    "cover_image_url",
    "thumbnail_url",
  ];

  for (const key of possibleKeys) {
    const value = announcement[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return null;
};

const toNamedItems = (items) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    label: item?.name ?? `#${item?.id ?? item}`,
  }));

const getAnnouncementAudience = (announcement) => {
  const clients = toNamedItems(announcement.target_clients);
  let clientMode = CLIENT_MODE_NONE;
  if (announcement.all_clients) clientMode = CLIENT_MODE_ALL;
  else if (clients.length > 0) clientMode = CLIENT_MODE_SELECTED;
  return {
    scope:
      announcement.target_agency_type ||
      (announcement.all ? AGENCY_SCOPE_ALL : AGENCY_SCOPE_MANUAL),
    agencies: toNamedItems(announcement.target_agencies),
    sendToConsultants: Boolean(announcement.send_to_consultants),
    clientMode,
    clients,
  };
};

const formatAnnouncementDate = (createdAt) => {
  if (!createdAt) return "Sin fecha";
  return new Date(createdAt).toLocaleString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const containedTextSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const linkTextSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  lineHeight: 1.45,
  flexShrink: 0,
};

const scrollAreaSx = {
  overflowY: "auto",
  overflowX: "hidden",
  minWidth: 0,
  minHeight: 0,
  maxWidth: "100%",
};

const AnnouncementLinkSection = ({ label, href, rawValue }) => {
  if (!rawValue) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Link2 size={16} />
        {label}
      </Typography>
      <Typography
        component="a"
        href={href || rawValue}
        target="_blank"
        rel="noopener noreferrer"
        title={rawValue}
        sx={{
          color: "#0b63f4",
          display: "block",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
          ...linkTextSx,
        }}
      >
        {rawValue}
      </Typography>
    </Box>
  );
};
AnnouncementLinkSection.propTypes = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  rawValue: PropTypes.string,
};

AnnouncementLinkSection.defaultProps = {
  href: "",
  rawValue: "",
};

const AnnouncementDateSection = ({ createdAt, textColor }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Clock size={16} />
      Fecha de publicacion
    </Typography>
    <Typography variant="body2" sx={{ color: textColor }}>
      {formatAnnouncementDate(createdAt)}
    </Typography>
  </Box>
);
AnnouncementDateSection.propTypes = {
  createdAt: PropTypes.string,
  textColor: PropTypes.string,
};

AnnouncementDateSection.defaultProps = {
  createdAt: "",
  textColor: "",
};

export function AnnouncementDetailModal({
  open,
  onClose,
  announcement,
  announcements,
  initialIndex,
  onIndexChange,
  userRole,
  onEdit,
  onDelete,
  isDeleting = false,
  deleteError = null,
}) {
  const { theme, neutrals } = useTheme();
  const announcementList = React.useMemo(() => {
    if (Array.isArray(announcements) && announcements.length > 0) {
      return announcements.filter(Boolean);
    }
    return announcement ? [announcement] : [];
  }, [announcement, announcements]);
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const isSuperAdmin = userRole === "superadmin";
  const isAgencyAdmin = userRole === "agency_admin";
  const isConsultant = userRole === "consultant";
  const isClient = userRole === "client";
  const canViewAnnouncementLinks =
    isSuperAdmin || isAgencyAdmin || isConsultant || isClient;
  const hasCarousel = announcementList.length > 1;

  React.useEffect(() => {
    if (!open) return;
    const boundedIndex = Math.max(
      0,
      Math.min(
        Number.isFinite(initialIndex) ? initialIndex : 0,
        Math.max(announcementList.length - 1, 0)
      )
    );
    setCurrentIndex(boundedIndex);
  }, [announcementList.length, initialIndex, open]);

  React.useEffect(() => {
    if (typeof onIndexChange === "function") {
      onIndexChange(currentIndex);
    }
  }, [currentIndex, onIndexChange]);

  const currentAnnouncement = announcementList[currentIndex] || null;
  const previousIndex =
    announcementList.length > 0
      ? (currentIndex - 1 + announcementList.length) % announcementList.length
      : 0;
  const nextIndex =
    announcementList.length > 0
      ? (currentIndex + 1) % announcementList.length
      : 0;
  const previousAnnouncement = announcementList[previousIndex] || null;
  const nextAnnouncement = announcementList[nextIndex] || null;

  const goToPreviousAnnouncement = React.useCallback(() => {
    if (!hasCarousel) return;
    setCurrentIndex((prev) => (prev - 1 + announcementList.length) % announcementList.length);
  }, [announcementList.length, hasCarousel]);

  const goToNextAnnouncement = React.useCallback(() => {
    if (!hasCarousel) return;
    setCurrentIndex((prev) => (prev + 1) % announcementList.length);
  }, [announcementList.length, hasCarousel]);

  const imageUrl = getAnnouncementImageUrl(currentAnnouncement);
  const bodyImageUrl =
    typeof currentAnnouncement?.body_image_url === "string" &&
      currentAnnouncement.body_image_url.trim()
      ? currentAnnouncement.body_image_url
      : null;
  const isAppLayoutAnnouncementsFlow =
    Array.isArray(announcements) && announcements.length > 0 && !announcement;
  const isMultipleAppLayoutFlow = isAppLayoutAnnouncementsFlow && hasCarousel;

  const handleClose = () => {
    onClose?.();
  };

  const modalTitle = isMultipleAppLayoutFlow
    ? "Nuevos anuncios"
    : isAppLayoutAnnouncementsFlow
      ? currentAnnouncement?.title
        ? `Nuevo anuncio: ${currentAnnouncement.title}`
        : "Nuevo anuncio"
      : "Detalle del anuncio";

  const renderCarouselPreviewCard = (item, variant, label = "") => {
    if (!item) return null;
    const previewImage = getAnnouncementImageUrl(item);
    const isMain = variant === "main";

    return (
      <Box
        sx={{
          position: "relative",
          borderRadius: "12px",
          overflow: "hidden",
          width: isMain ? { xs: 240, sm: 300, md: 340 } : { xs: 92, sm: 110, md: 250 },
          height: isMain ? { xs: 132, sm: 156, md: 190 } : { xs: 112, sm: 132, md: 180 },
          flexShrink: 0,
          background: previewImage
            ? "none"
            : "linear-gradient(135deg, rgba(2,132,199,0.28), rgba(15,23,42,0.55))",
          border: `1px solid ${theme.palette.divider}`,
          cursor: isMain ? "default" : "pointer",
        }}
      >
        {previewImage && (
          <Box
            component="img"
            src={previewImage}
            alt={item?.title || "Anuncio"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              filter: isMain ? "none" : "grayscale(20%)",
            }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: isMain
              ? "rgba(15,23,42,0.2)"
              : "rgba(15,23,42,0.55)",
          }}
        />
        {!isMain && label && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              color: "#fff",
              fontWeight: 700,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              fontSize: "0.68rem",
            }}
          >
            {label}
          </Typography>
        )}
        <Typography
          variant={isMain ? "subtitle2" : "caption"}
          sx={{
            position: "absolute",
            left: 10,
            right: 10,
            bottom: 10,
            color: "#fff",
            fontWeight: isMain ? 700 : 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={item?.title || ""}
        >
          {item?.title || ""}
        </Typography>
      </Box>
    );
  };

  const renderCarouselModalCard = (item, isActive = false) => {
    if (!item) return null;

    const cardImageUrl = getAnnouncementImageUrl(item);

    return (
      <Box
        sx={{
          width: { xs: 270, sm: 320, md: 360 },
          height: { xs: 360, sm: 430, md: 470 },
          borderRadius: "12px",
          border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: isActive
            ? "0 16px 36px rgba(15, 23, 42, 0.16)"
            : "0 10px 24px rgba(15, 23, 42, 0.10)",
          overflow: "hidden",
          flexShrink: 0,
          cursor: "pointer",
          opacity: isActive ? 1 : 0.74,
          transform: isActive ? "scale(1)" : "scale(0.94)",
          transition: "all 0.2s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 64,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              pr: 1,
            }}
            title={item?.title || ""}
          >
            {item?.title || ""}
          </Typography>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {cardImageUrl && (
            <Box
              component="img"
              src={cardImageUrl}
              alt={item?.title || "Anuncio"}
              sx={{
                width: "100%",
                height: 160,
                objectFit: "cover",
                borderRadius: "10px",
                display: "block",
              }}
            />
          )}
          <AnnouncementDescription
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.45,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 5,
              WebkitBoxOrient: "vertical",
            }}
          >
            {item?.description ?? ""}
          </AnnouncementDescription>
                {canViewAnnouncementLinks && (item?.links || []).map((url, index) => (
                  <Box key={`${index}-${url}`} sx={{ mt: 1, minWidth: 0 }}>
                    <AnnouncementLinkSection label={`Enlace ${index + 1}`} href={normalizeUrlWithHttps(url)} rawValue={url} />
                  </Box>
                ))}

          <Stack direction="row" gap={1} flexWrap="wrap">

          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              mt: "auto",
              fontWeight: 600,
            }}
          >
            {formatAnnouncementDate(item?.created_at)}
          </Typography>
        </Box>
      </Box>
    );
  };

  React.useEffect(() => {
    if (!open || !hasCarousel) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        goToPreviousAnnouncement();
      } else if (event.key === "ArrowRight") {
        goToNextAnnouncement();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [goToNextAnnouncement, goToPreviousAnnouncement, hasCarousel, open]);

  if (isMultipleAppLayoutFlow) {
    const carouselCardHeight = {
      xs: "min(calc(100vh - 180px), 560px)",
      sm: "min(calc(100vh - 170px), 640px)",
      md: "min(calc(100vh - 160px), 720px)",
    };
    const carouselCardRadius = "12px";

    const renderCarouselFullCard = (item) => {
      if (!item) return null;

      const cardImageUrl = getAnnouncementImageUrl(item);
      const cardTitle = item?.title
        ? `Nuevo anuncio: ${item.title}`
        : "Nuevo anuncio";

      const cardBodyImageUrl =
        typeof item?.body_image_url === "string" && item.body_image_url.trim()
          ? item.body_image_url
          : null;

      return (
        <Box
          sx={{
            width: { xs: "calc(100vw - 48px)", sm: 560, md: 600 },
            maxWidth: "calc(100vw - 48px)",
            maxHeight: carouselCardHeight,
            height: carouselCardHeight,
            borderRadius: carouselCardRadius,
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: { xs: 2, sm: 2.25 },
              pb: { xs: 1, sm: 1.5 },
              minHeight: 72,
              display: "flex",
              alignItems: "center",
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
                lineHeight: 1.3,
                color: theme.palette.text.primary,
                ...containedTextSx,
              }}
            >
              {cardTitle}
            </Typography>
          </Box>

          {cardImageUrl && (
            <Box
              component="img"
              src={cardImageUrl}
              alt={item?.title || "Anuncio"}
              sx={{
                width: "100%",
                height: { xs: 200, sm: 240, md: 280 },
                objectFit: "contain",
                flexShrink: 0,
                display: "block",
                backgroundColor: neutrals.gray[100],
              }}
            />
          )}

          <Box
            sx={{
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              flex: 1,
              minHeight: 0,
              ...scrollAreaSx,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: neutrals.gray[100],
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: neutrals.gray[300],
                borderRadius: "3px",
              },
            }}
          >
            <AnnouncementDescription
              sx={{
                whiteSpace: "normal",
                lineHeight: 1.5,
                mb: 1,
                color: theme.palette.text.secondary,
                ...containedTextSx,
              }}
            >
              {item?.description ?? ""}
            </AnnouncementDescription>
                {canViewAnnouncementLinks && (item?.links || []).map((url, index) => (
                  <Box key={`${index}-${url}`} sx={{ mt: 1, minWidth: 0 }}>
                    <AnnouncementLinkSection label={`Enlace ${index + 1}`} href={normalizeUrlWithHttps(url)} rawValue={url} />
                  </Box>
                ))}

            {cardBodyImageUrl && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1,
                  mb: 1,
                }}
              >
                <Box
                  component="img"
                  src={cardBodyImageUrl}
                  alt={item?.title || "Imagen del anuncio"}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "260px",
                    borderRadius: "8px",
                    display: "block",
                  }}
                />
              </Box>
            )}

            {canViewAnnouncementLinks && (
              <Box sx={{ flexShrink: 0, minWidth: 0, maxWidth: "100%" }}>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={1.5} sx={{ minWidth: 0, maxWidth: "100%" }}>
                  <Grid
                      container
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ width: "100%", minWidth: 0 }}
                    >

                      <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                        <AnnouncementDateSection
                          createdAt={item?.created_at}
                          textColor={theme.palette.text.secondary}
                        />
                      </Grid>
                    </Grid>

                  {isSuperAdmin && item && (
                    <AnnouncementAudienceSummary
                      title="Destinatarios"
                      {...getAnnouncementAudience(item)}
                    />
                  )}
                </Stack>
              </Box>
            )}

            {!canViewAnnouncementLinks && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontWeight: 500, mt: "auto" }}
              >
                {formatAnnouncementDate(item?.created_at)}
              </Typography>
            )}
          </Box>
        </Box>
      );
    };

    const SIDE_OFFSET_X = 450;
    const SIDE_SCALE = 0.82;
    const SIDE_OPACITY = 0.45;
    const ARROW_SX = {
      color: "#fff",
      backgroundColor: "rgba(255,255,255,0.18)",
      width: 52,
      height: 52,
      "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
    };
    // Laterales en escritorio; en móvil quedarían encima de la tarjeta (blanco sobre blanco)
    const SIDE_ARROW_SX = {
      ...ARROW_SX,
      display: { xs: "none", sm: "inline-flex" },
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 10,
    };

    return (
      <Modal
        open={open}
        onClose={handleClose}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(2, 6, 23, 0.9)",
            backdropFilter: "blur(1.5px)",
          },
        }}
      >
        <Box
          tabIndex={-1}
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
            overflowX: "hidden",
            overflowY: "auto",
            "&:focus": { outline: "none" },
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={handleClose}
            aria-label="Cerrar anuncios"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 10,
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
            }}
          >
            <X size={20} />
          </IconButton>

          {/* Counter */}
          <Typography
            variant="overline"
            sx={{
              color: "rgba(255,255,255,0.75)",
              letterSpacing: "0.12em",
              mb: 2,
              fontWeight: 600,
              userSelect: "none",
            }}
          >
            Nuevos anuncios &nbsp;·&nbsp; {currentIndex + 1} / {announcementList.length}
          </Typography>

          {/* Carousel track */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: { xs: "calc(100dvh - 190px)", sm: "calc(100vh - 140px)", md: "calc(100vh - 120px)" },
              overflow: "hidden",
            }}
          >
            {/* Previous card — hidden on mobile */}
            <Box
              onClick={goToPreviousAnnouncement}
              sx={{
                display: { xs: "none", sm: "block" },
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: `translate(calc(-50% - ${SIDE_OFFSET_X}px), -50%) scale(${SIDE_SCALE})`,
                opacity: SIDE_OPACITY,
                zIndex: 1,
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {renderCarouselFullCard(previousAnnouncement)}
            </Box>

            {/* Next card — hidden on mobile */}
            <Box
              onClick={goToNextAnnouncement}
              sx={{
                display: { xs: "none", sm: "block" },
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: `translate(calc(-50% + ${SIDE_OFFSET_X}px), -50%) scale(${SIDE_SCALE})`,
                opacity: SIDE_OPACITY,
                zIndex: 1,
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {renderCarouselFullCard(nextAnnouncement)}
            </Box>

            {/* Current (center) card */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 3,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {renderCarouselFullCard(currentAnnouncement)}
            </Box>
          </Box>

          {/* Dots: en móvil no caben, basta el contador de arriba */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1, mt: 2.5, alignItems: "center" }}>
            {announcementList.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                sx={{
                  width: idx === currentIndex ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor:
                    idx === currentIndex ? "#fff" : "rgba(255,255,255,0.4)",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.8)" },
                }}
              />
            ))}
          </Box>

          {/* Mobile arrows, below the card */}
          <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 3, mt: 2 }}>
            <IconButton onClick={goToPreviousAnnouncement} aria-label="Anuncio anterior" sx={ARROW_SX}>
              <ChevronLeft size={28} />
            </IconButton>
            <IconButton onClick={goToNextAnnouncement} aria-label="Siguiente anuncio" sx={ARROW_SX}>
              <ChevronRight size={28} />
            </IconButton>
          </Box>

          <IconButton
            onClick={goToPreviousAnnouncement}
            aria-label="Anuncio anterior"
            sx={{ ...SIDE_ARROW_SX, left: { sm: 20, md: 40 } }}
          >
            <ChevronLeft size={28} />
          </IconButton>
          <IconButton
            onClick={goToNextAnnouncement}
            aria-label="Siguiente anuncio"
            sx={{ ...SIDE_ARROW_SX, right: { sm: 20, md: 40 } }}
          >
            <ChevronRight size={28} />
          </IconButton>
        </Box>
      </Modal>
    );
  }

  return (
    <UnifiedModal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      maxWidth="sm"
      BackdropProps={
        isMultipleAppLayoutFlow
          ? {
            sx: {
              backgroundColor: "rgba(2, 6, 23, 0.88)",
            },
          }
          : undefined
      }
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
      contentSx={{
        pt: "16px",
        overflowX: "hidden",
      }}
      footer={
        isSuperAdmin && (onEdit || onDelete) ? (
          <>
            {deleteError && (
              <Typography sx={{ color: theme.palette.error.main, mb: 1.5 }}>
                {deleteError}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              {onEdit && (
                <Button variant="outline" onClick={onEdit}>
                  Editar
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="destructive"
                  onClick={onDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Eliminando..." : "Eliminar"}
                </Button>
              )}
            </Box>
          </>
        ) : (isAgencyAdmin || isConsultant || isClient) &&
          !isMultipleAppLayoutFlow ? (
          <Grid container spacing={2} alignItems="flex-start" sx={{ width: "100%", minWidth: 0 }}>

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <AnnouncementDateSection
                createdAt={currentAnnouncement?.created_at}
                textColor={theme.palette.text.secondary}
              />
            </Grid>
          </Grid>
        ) : null
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md:
              hasCarousel && !isMultipleAppLayoutFlow
                ? "minmax(0, 1fr) 260px"
                : "1fr",
          },
          gap: {
            xs: 0,
            md: hasCarousel && !isMultipleAppLayoutFlow ? 2 : 0,
          },
          alignItems: "start",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            overflowX: "hidden",
            minWidth: 0,
            pr: 1,
            pb: 3,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: neutrals.gray[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: neutrals.gray[300],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: neutrals.gray[400],
              },
            },
          }}
        >
          <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
            {hasCarousel && isMultipleAppLayoutFlow && (
              <Box sx={{ mb: 2, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    maxWidth: "100%",
                    pb: 1,
                    px: 0.5,
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: neutrals.gray[100],
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: neutrals.gray[300],
                      borderRadius: "4px",
                    },
                  }}
                >
                  {announcementList.map((item, index) => (
                    <React.Fragment
                      key={item?.id ?? item?.pk ?? `announcement-modal-${index}`}
                    >
                      {index > 0 && (
                        <IconButton
                          onClick={() => setCurrentIndex(index)}
                          aria-label={`Ir al anuncio ${index + 1}`}
                          sx={{
                            backgroundColor: "rgba(15,23,42,0.08)",
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": { backgroundColor: "rgba(15,23,42,0.16)" },
                          }}
                        >
                          <ChevronRight size={16} />
                        </IconButton>
                      )}

                      <Box onClick={() => setCurrentIndex(index)}>
                        {renderCarouselModalCard(item, index === currentIndex)}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 1,
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                  }}
                >
                  {currentIndex + 1} de {announcementList.length}
                </Typography>
              </Box>
            )}

            {hasCarousel && !isMultipleAppLayoutFlow && (
              <Box
                sx={{
                  mb: 2,
                  position: "relative",
                  display: { xs: "block", md: "none" },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  <Box onClick={goToPreviousAnnouncement}>
                    {renderCarouselPreviewCard(previousAnnouncement, "side", "Anterior")}
                  </Box>
                  {renderCarouselPreviewCard(currentAnnouncement, "main")}
                  <Box onClick={goToNextAnnouncement}>
                    {renderCarouselPreviewCard(nextAnnouncement, "side", "Siguiente")}
                  </Box>
                </Stack>

                <IconButton
                  onClick={goToPreviousAnnouncement}
                  aria-label="Anterior anuncio"
                  sx={{
                    position: "absolute",
                    left: { xs: 0, sm: 8 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(255,255,255,0.85)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                  }}
                >
                  <ChevronLeft size={18} />
                </IconButton>
                <IconButton
                  onClick={goToNextAnnouncement}
                  aria-label="Siguiente anuncio"
                  sx={{
                    position: "absolute",
                    right: { xs: 0, sm: 8 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(255,255,255,0.85)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                  }}
                >
                  <ChevronRight size={18} />
                </IconButton>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 1,
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                  }}
                >
                  {currentIndex + 1} de {announcementList.length}
                </Typography>
              </Box>
            )}

            {!hasCarousel && imageUrl && (
              <Box
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt={currentAnnouncement?.title || "Imagen del anuncio"}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "300px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            )}

            {!isMultipleAppLayoutFlow && (
              <>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 0.5, ...containedTextSx }}
                >
                  {currentAnnouncement?.title || ""}
                </Typography>

                <AnnouncementDescription
                  sx={{
                    whiteSpace: "normal",
                    lineHeight: 1.5,
                    mb: 1,
                    color: theme.palette.text.secondary,
                    ...containedTextSx,
                  }}
                >
                  {currentAnnouncement?.description ?? ""}
                </AnnouncementDescription>
                {canViewAnnouncementLinks && (currentAnnouncement?.links || []).map((url, index) => (
                  <Box key={`${index}-${url}`} sx={{ mt: 1, minWidth: 0 }}>
                    <AnnouncementLinkSection label={`Enlace ${index + 1}`} href={normalizeUrlWithHttps(url)} rawValue={url} />
                  </Box>
                ))}

                {bodyImageUrl && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={bodyImageUrl}
                      alt={currentAnnouncement?.title || "Imagen del anuncio"}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "360px",
                        borderRadius: "8px",
                        display: "block",
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

          {isSuperAdmin && !isMultipleAppLayoutFlow && (
            <Box sx={{ flexShrink: 0, minWidth: 0, maxWidth: "100%" }}>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2} sx={{ minWidth: 0, maxWidth: "100%" }}>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    sx={{ width: "100%", minWidth: 0 }}
                  >

                    <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
                      <AnnouncementDateSection
                        createdAt={currentAnnouncement?.created_at}
                        textColor={theme.palette.text.secondary}
                      />
                    </Grid>
                  </Grid>

                {currentAnnouncement && (
                  <AnnouncementAudienceSummary
                    title="Destinatarios"
                    {...getAnnouncementAudience(currentAnnouncement)}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Box>

        {hasCarousel && !isMultipleAppLayoutFlow && (
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "stretch",
              gap: 1.5,
              pt: 0.5,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Box
              onClick={goToPreviousAnnouncement}
              sx={{ cursor: "pointer", alignSelf: "flex-end" }}
            >
              {renderCarouselPreviewCard(previousAnnouncement, "side", "Anterior")}
            </Box>
            <Box
              onClick={goToNextAnnouncement}
              sx={{ cursor: "pointer", alignSelf: "flex-end" }}
            >
              {renderCarouselPreviewCard(nextAnnouncement, "side", "Siguiente")}
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={1}
              sx={{ mt: 0.5 }}
            >
              <IconButton
                onClick={goToPreviousAnnouncement}
                aria-label="Anterior anuncio"
                sx={{
                  backgroundColor: "rgba(15,23,42,0.08)",
                  "&:hover": { backgroundColor: "rgba(15,23,42,0.16)" },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>
              <IconButton
                onClick={goToNextAnnouncement}
                aria-label="Siguiente anuncio"
                sx={{
                  backgroundColor: "rgba(15,23,42,0.08)",
                  "&:hover": { backgroundColor: "rgba(15,23,42,0.16)" },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Stack>

            <Typography
              variant="caption"
              sx={{
                textAlign: "right",
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            >
              {currentIndex + 1} de {announcementList.length}
            </Typography>
          </Box>
        )}
      </Box>

    </UnifiedModal>
  );
}

AnnouncementDetailModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  announcements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      title: PropTypes.string,
      description: PropTypes.string,
      all: PropTypes.bool,
      created_at: PropTypes.string,
      links: PropTypes.arrayOf(PropTypes.string),
      target_agencies: PropTypes.array,
      target_agencies_details: PropTypes.array,
      header_image_url: PropTypes.string,
      body_image_url: PropTypes.string,
      image_url: PropTypes.string,
      image: PropTypes.string,
      cover_image_url: PropTypes.string,
      thumbnail_url: PropTypes.string,
    })
  ),
  initialIndex: PropTypes.number,
  onIndexChange: PropTypes.func,
  announcement: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    description: PropTypes.string,
    all: PropTypes.bool,
    created_at: PropTypes.string,
    links: PropTypes.arrayOf(PropTypes.string),
    target_agencies: PropTypes.array,
    target_agencies_details: PropTypes.array,
    header_image_url: PropTypes.string,
    image_url: PropTypes.string,
    image: PropTypes.string,
    cover_image_url: PropTypes.string,
    thumbnail_url: PropTypes.string,
  }),
  userRole: PropTypes.string,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  isDeleting: PropTypes.bool,
  deleteError: PropTypes.string,
};

AnnouncementDetailModal.defaultProps = {
  open: false,
  onClose: undefined,
  announcements: undefined,
  initialIndex: 0,
  onIndexChange: undefined,
  announcement: null,
  userRole: "",
  onEdit: undefined,
  onDelete: undefined,
  isDeleting: false,
  deleteError: null,
};
